import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Rating as MuiRating,
  Chip,
  Divider,
  alpha,
  Avatar
} from '@mui/material';
import { Directions, Phone, Language, Schedule, Place, Coffee } from '@mui/icons-material';
import { Cafe } from '../../types/explore';
import CafePhotos from './CafePhotos';

interface CafeCardProps {
  cafe: Cafe;
  onNavigate: () => void;
  darkMode: boolean;
}

const CafeCard: React.FC<CafeCardProps> = ({ cafe, onNavigate, darkMode }) => {
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  const goldGradient = 'linear-gradient(135deg, #C4B66F 0%, #D4C68A 100%)';
  
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  return (
    <Card 
      elevation={0}
      sx={{ 
        width: '100%', 
        maxWidth: 450, // Safe max width
        borderRadius: '24px',
        background: darkMode 
          ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.95) 0%, rgba(15, 59, 46, 0.95) 100%)'
          : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,240,240,0.95) 100%)',
        border: `1px solid ${alpha(darkMode ? goldColor : deepGreen, 0.1)}`,
        boxShadow: darkMode ? '0 20px 50px rgba(0,0,0,0.5)' : '0 10px 40px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        // Center the card itself if used in a flex container
        mx: 'auto'
      }}
    >
      {/* Cafe Header */}
      <Box sx={{ 
        p: 3, 
        background: lokiGradient,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <Avatar sx={{ 
          width: 60, 
          height: 60, 
          bgcolor: goldColor,
          color: deepGreen,
          fontWeight: 'bold'
        }}>
          <Coffee />
        </Avatar>
        
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              ...discordFont,
              color: '#fff',
              fontSize: '1.25rem',
              mb: 0.5,
              lineHeight: 1.2
            }}
          >
            {cafe.name}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Place sx={{ fontSize: 16, color: goldColor, opacity: 0.8 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: alpha('#fff', 0.8),
                fontSize: '0.85rem'
              }}
            >
              {cafe.address.length > 30 ? `${cafe.address.substring(0, 30)}...` : cafe.address}
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <CardContent sx={{ p: 3 }}>
        {/* Rating and Info */}
        {cafe.rating !== undefined && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ 
                color: darkMode ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                fontWeight: 600 
              }}>
                Overall Rating
              </Typography>
              <Chip
                label={`${cafe.rating.toFixed(1)}/5`}
                size="small"
                sx={{
                  background: goldGradient,
                  color: deepGreen,
                  fontWeight: 700,
                  fontSize: '0.85rem'
                }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <MuiRating 
                value={cafe.rating} 
                readOnly 
                size="medium" 
                precision={0.5}
                sx={{ color: goldColor }}
              />
              {cafe.userRatingsTotal && (
                <Typography variant="caption" sx={{ 
                  color: darkMode ? alpha('#fff', 0.5) : alpha(deepGreen, 0.5) 
                }}>
                  ({cafe.userRatingsTotal} reviews)
                </Typography>
              )}
            </Box>
          </Box>
        )}
        
        {/* Tags */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 4 }}>
          {cafe.openingHours?.openNow !== undefined && (
            <Chip
              icon={<Schedule sx={{ fontSize: 14 }} />}
              label={cafe.openingHours.openNow ? 'Open Now' : 'Closed'}
              size="small"
              color={cafe.openingHours.openNow ? 'success' : 'error'}
              variant="filled"
              sx={{ 
                fontWeight: 600,
                fontSize: '0.75rem',
                background: cafe.openingHours.openNow 
                  ? 'linear-gradient(135deg, #23A559 0%, #0F3B2E 100%)'
                  : 'linear-gradient(135deg, #DA373C 0%, #0F3B2E 100%)',
                color: '#fff'
              }}
            />
          )}
          
          {cafe.priceLevel && (
            <Chip
              label={'$'.repeat(cafe.priceLevel)}
              size="small"
              sx={{ 
                color: goldColor, 
                borderColor: goldColor,
                fontSize: '0.75rem',
                fontWeight: 600,
                background: alpha(goldColor, 0.1)
              }}
            />
          )}
        </Box>

        {cafe.photos && cafe.photos.length > 0 && (
  <CafePhotos 
    photos={cafe.photos} 
    cafeName={cafe.name}
    darkMode={darkMode}
    maxVisible={3}
  />
)}
        
        <Divider sx={{ 
          my: 3, 
          borderColor: alpha(darkMode ? goldColor : deepGreen, 0.1) 
        }} />
        
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Directions />}
            onClick={onNavigate}
            sx={{
              borderRadius: '16px',
              background: goldGradient,
              color: deepGreen,
              fontWeight: 700,
              py: 1.5,
              fontSize: '1rem',
              boxShadow: '0 4px 15px rgba(196, 182, 111, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #D4C68A 0%, #E5D6A5 100%)',
                boxShadow: '0 6px 20px rgba(196, 182, 111, 0.4)'
              }
            }}
          >
            Navigate
          </Button>
          
          {cafe.website && (
            <Button
              variant="outlined"
              href={cafe.website}
              target="_blank"
              sx={{
                minWidth: 'auto',
                borderRadius: '16px',
                borderColor: goldColor,
                color: goldColor,
                px: 2,
                '&:hover': {
                  borderColor: goldColor,
                  backgroundColor: alpha(goldColor, 0.1)
                }
              }}
            >
              <Language />
            </Button>
          )}
        </Box>
        
        {/* Contact Info */}
        {cafe.phoneNumber && (
          <Box sx={{ 
            mt: 3, 
            pt: 3, 
            borderTop: `1px dashed ${alpha(darkMode ? goldColor : deepGreen, 0.2)}` 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Phone sx={{ 
                fontSize: 16, 
                color: goldColor, 
                p: 1, 
                bgcolor: alpha(goldColor, 0.1), 
                borderRadius: '8px' 
              }} />
              <Typography variant="body2" sx={{ 
                color: darkMode ? alpha('#fff', 0.8) : alpha(deepGreen, 0.8),
                fontWeight: 500
              }}>
                {cafe.phoneNumber}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CafeCard;